import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useEffect, useState } from 'react';

const CursorFollower = () => {
  const [visible, setVisible] = useState(false);
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  /** Inner dot — snappy, stays close to the real cursor */
  const dotSpring = { stiffness: 720, damping: 42, mass: 0.12 };
  /** Outer ring — softer trail for depth */
  const ringSpring = { stiffness: 210, damping: 26, mass: 0.38 };

  const ringSize = 44;
  const dotSize = 6;

  // Bound to x/y (transform: translate) instead of left/top so the browser
  // never has to run layout on mousemove — compositor-only, no forced reflow.
  const dotXSpring = useSpring(cursorX, dotSpring);
  const dotYSpring = useSpring(cursorY, dotSpring);
  const ringXSpring = useSpring(cursorX, ringSpring);
  const ringYSpring = useSpring(cursorY, ringSpring);
  const dotX = useTransform(dotXSpring, (v) => v - dotSize / 2);
  const dotY = useTransform(dotYSpring, (v) => v - dotSize / 2);
  const ringX = useTransform(ringXSpring, (v) => v - ringSize / 2);
  const ringY = useTransform(ringYSpring, (v) => v - ringSize / 2);

  useEffect(() => {
    const moveCursor = (e: MouseEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      if (!visible) setVisible(true);
    };

    const leave = () => setVisible(false);

    window.addEventListener('mousemove', moveCursor);
    document.documentElement.addEventListener('mouseleave', leave);
    return () => {
      window.removeEventListener('mousemove', moveCursor);
      document.documentElement.removeEventListener('mouseleave', leave);
    };
  }, [cursorX, cursorY, visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] hidden lg:block overflow-hidden">
      {/* Trailing ring — softer lag */}
      <motion.div
        aria-hidden
        className="rounded-full will-change-transform"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          x: ringX,
          y: ringY,
          width: ringSize,
          height: ringSize,
          border: '1px solid hsl(var(--foreground) / 0.12)',
          background:
            'linear-gradient(145deg, hsl(var(--foreground) / 0.06), hsl(var(--foreground) / 0.02))',
          boxShadow:
            '0 0 0 1px hsl(var(--background) / 0.4) inset, 0 12px 40px hsl(var(--foreground) / 0.06)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
      />
      {/* Core dot — tight follow */}
      <motion.div
        aria-hidden
        className="rounded-full will-change-transform"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          x: dotX,
          y: dotY,
          width: dotSize,
          height: dotSize,
          background: 'hsl(var(--foreground) / 0.92)',
          boxShadow:
            '0 0 20px hsl(var(--foreground) / 0.35), 0 0 2px hsl(var(--background) / 0.9) inset',
        }}
      />
    </div>
  );
};

export default CursorFollower;
